"use client";

import { RAINBOW_KIT_PROJECT_ID } from "@/constants/wallet.constants";
import type { WagmiConfigType } from "@/providers/crypto.provider";
import { getChainsByConfigType } from "@/utils/wagmi/select-chains-by-config-type.util";
import { getDefaultConfig } from "@rainbow-me/rainbowkit";
import {
  mainnet,
  arbitrum,
  optimism,
  base,
  sepolia,
  optimismSepolia,
  arbitrumSepolia,
  baseSepolia,
} from "wagmi/chains";
import { createStorage, http } from "wagmi";
import { blessnetSepolia, blessnetMainnet } from "@/config/bless-chain";

/**
 * Uses the getDefautlConfig function from RainbowKit to create a configuration object
 *
 * Creates a configuration object  based on the specified Wagmi configuration type.
 *
 * @param configType - The type of Wagmi configuration to use. Determines the chains and storage key.
 * @returns A configuration object for RainbowKit with the specified settings.
 *
 * The configuration includes:
 * - The application name ("Blessnet").
 * - A project ID for RainbowKit integration.
 * - The chains determined by the provided configuration type.
 * - Server-side rendering (SSR) support enabled.
 * - A custom storage mechanism with a key based on the configuration type.
 */
export const createConfigWithRainbowKit = (configType: WagmiConfigType) => {
  const chains = getChainsByConfigType(configType);
  const isClient = typeof window !== "undefined";
  return getDefaultConfig({
    appName: "Blessnet",
    projectId: RAINBOW_KIT_PROJECT_ID,
    chains,
    transports: {
      [blessnetMainnet.id]: http(blessnetMainnet.rpcUrls.default.http[0]),
      [mainnet.id]: http(
        `https://eth-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
      [arbitrum.id]: http(
        `https://arb-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
      [optimism.id]: http(
        `https://opt-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
      [base.id]: http(
        `https://base-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
      [blessnetSepolia.id]: http(blessnetSepolia.rpcUrls.default.http[0]),
      [sepolia.id]: http(
        `https://eth-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
      [arbitrumSepolia.id]: http(
        `https://arb-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
      [optimismSepolia.id]: http(
        `https://opt-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
      [baseSepolia.id]: http(
        `https://base-sepolia.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`,
      ),
    },
    ssr: true,
    // Local storage is accessed only on the client side
    storage: isClient
      ? createStorage({
          storage: window.localStorage,
          key: configType,
        })
      : undefined,
  });
};
